<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { FeatureImportance, MatchingConfiguration, MatchingConfigurationReason } from '@swipe4work/api-client-fetch'
import { useEmployerApiClient } from '@/modules/api-clients/api-clients'
import { useTopbarStore } from '@/components/app/topbar-store'
import ProgressSpinner from 'primevue/progressspinner'
import MatchingConfigurationExplanation from './matching-configuration/components/MatchingConfigurationExplanation.vue'
import MatchingConfigurationConfirmationDialog
  from './matching-configuration/components/MatchingConfigurationConfirmationDialog.vue'
import { useConfirm } from 'primevue/useconfirm'

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const isLoading = ref(false)
const isFormLoading = ref(true)
const topbarStore = useTopbarStore()
const employerApiClient = useEmployerApiClient()

definePage({
  name: 'matching-configuration',
  path: '/matching-criteria',
})

topbarStore.setAndClear({
  breadcrumbs: [ { label: t('matchingConfiguration.title') } ],
})

const defaultMatchingConfiguration: MatchingConfiguration = {
  workExperienceImportance: FeatureImportance.Average,
  educationImportance: FeatureImportance.Average,
  hardSkillsImportance: FeatureImportance.Average,
  softSkillsImportance: FeatureImportance.Average,
  personalityImportance: FeatureImportance.Average,
}
const model = ref({
  matchingConfiguration: defaultMatchingConfiguration,
  reason: undefined as MatchingConfigurationReason | undefined,
})

onMounted(async () => {
  isFormLoading.value = true

  const [ err, response ] = await safeResolve(employerApiClient.getMatchingConfiguration())

  isFormLoading.value = false
  if (err) {
    console.error('Failed to fetch matching configuration:', err)
    toast.add({
      severity: 'error',
      summary: t('matchingConfiguration.errors.fetchFailed'),
      life: 5000,
    })
    // Fallback to default on error
    model.value.matchingConfiguration = { ...defaultMatchingConfiguration }
    isFormLoading.value = false

    return
  }

  if (response.matchingConfiguration) {
    model.value.matchingConfiguration = response.matchingConfiguration
  } else {
    // There is no custom matching configuration, so we use the default
    model.value.matchingConfiguration = { ...defaultMatchingConfiguration }
  }
})

const resetFormToDefaults = async () => {
  confirm.require({
    target: document.body,
    modal: true,
    message: t('matchingConfiguration.reset.confirmation'),
    header: t('matchingConfiguration.reset.confirmationTitle'),
    accept: async () => {
      isLoading.value = true
      const [ err ] = await safeResolve(employerApiClient.resetMatchingConfiguration())

      isLoading.value = false

      if (err) {
        console.error('Failed to reset matching configuration:', err)
        toast.add({
          severity: 'error',
          summary: t('general.errorOccurred'),
          detail: t('matchingConfiguration.errors.resetFailed'),
          life: 5000,
        })

        return
      }

      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'De matching criteria zijn succesvol teruggezet naar de standaardwaarden.',
        life: 3000,
      })

      // Reset the form to default values
      model.value.matchingConfiguration = { ...defaultMatchingConfiguration }
    },
    acceptLabel: t('confirm'),
    rejectLabel: t('cancel'),
  })
}

const validateNotAllSameImportance = (values: MatchingConfiguration) => {
  if (!values) return true

  const importanceValues = [
    values.workExperienceImportance,
    values.educationImportance,
    values.hardSkillsImportance,
    values.softSkillsImportance,
    values.personalityImportance,
  ]

  const allImportant = importanceValues.every(value => value === FeatureImportance.Important)
  const allNotImportant = importanceValues.every(value => value === FeatureImportance.NotImportant)

  return !(allImportant || allNotImportant)
}

const onFormSubmit = async (reason: MatchingConfigurationReason) => {
  const currentMatchingConfig = model.value.matchingConfiguration
  const isConfigValid = validateNotAllSameImportance(currentMatchingConfig)

  if (!isConfigValid) {
    toast.add({
      severity: 'error',
      summary: t('matchingConfiguration.validation.formInvalid'),
      life: 5000,
    })

    return
  }

  isLoading.value = true
  try {
    await employerApiClient.setMatchingConfiguration({
      matchingConfiguration: currentMatchingConfig,
      reason, // Use the reason passed from the confirmation dialog
    })
    toast.add({
      severity: 'success',
      summary: t('matchingConfiguration.success.saveTitle'),
      detail: t('matchingConfiguration.success.saveDetail'),
      life: 3000,
    })
  } catch (error) {
    console.error('Failed to save matching configuration:', error)
    toast.add({
      severity: 'error',
      summary: t('general.errorOccurred'),
      detail: t('matchingConfiguration.errors.saveFailed'),
      life: 5000,
    })
  } finally {
    isLoading.value = false
  }
}

const dialog = useDynamicDialog()

const openExplanation = () => {
  dialog.open(MatchingConfigurationExplanation, {
    props: {
      style: {
        width: '50rem',
        height: 'auto',
      },
    },
  })
}

const openConfirm = () => {
  const dialogRef = dialog.open(MatchingConfigurationConfirmationDialog, {
    emits: {
      onSave: async (reason: MatchingConfigurationReason ) => {
        await onFormSubmit(reason)

        dialogRef.close()
      },
      onCancel: () => {
        dialogRef.close()
      },
    },
    props: {
      style: {
        width: '50rem',
        height: 'auto',
      },
    },
  })
}
</script>

<template>
  <div v-if="isFormLoading" class="flex items-center justify-center p-8">
    <ProgressSpinner
      style="width:50px;height:50px"
      stroke-width="8"
      animation-duration=".5s"
      aria-label="Loading configuration"
    />
  </div>
  <template v-else-if="model">
    <MatchingConfigurationForm
      @submit="openConfirm"
      v-model="model.matchingConfiguration"
      :is-loading="isLoading"
      @reset="resetFormToDefaults"
      @explain="openExplanation"
    />
  </template>
  <div v-else class="p-4 text-center">
    <p>{{ $t('matchingConfiguration.errors.loadFailedFallback') }}</p>
  </div>
</template>